import { FC } from "react";
import {
    Box,
    Heading,
    Text,
    Flex,
    Icon,
    Button,
} from "@smile2impress/components/relaunch";
import { useGoToBooking } from "hooks/useGoToBooking";

import Link from "components/Link";

import { MainBannerHeaderProps } from "./MainBannerHeader.types";

export const MainBannerHeader: FC<MainBannerHeaderProps> = ({
    title,
    subtitle,
    text,
    cta,
    headingAs = "h2",
    headingTextStyle = "H1",
    withImage,
    dataTestId,
    extraStyles,
}) => {
    const { goToBooking } = useGoToBooking();

    const {
        text: ctaButtonLabel,
        link,
        iconLeft,
        iconRight,
        variant,
    } = cta ?? {};
    const onClick = cta?.onClick ?? (link ? undefined : goToBooking);

    const titleColor =
        extraStyles?.title ??
        (withImage
            ? ["text.baseInverse", "text.baseInverse", "text.primary"]
            : "text.primary");

    return (
        <Box {...(!withImage && { px: [0, 0, 4], pt: [3, 3, 0] })}>
            <Heading
                as={headingAs}
                textStyle={headingTextStyle}
                maxW={["100%", "100%", "800px"]}
                color={titleColor}
                {...(!subtitle && { mb: [0.7, 0.7, 2] })}
                _dark={{
                    color: withImage
                        ? ["text.base", "text.base", "text.primary"]
                        : "text.primary",
                }}
                sx={{
                    // For some mandatory, but not visual appealing symbols, e.g. ®
                    "& > sup": {
                        fontSize: ["20px", "20px", "36px"],
                        fontWeight: ["light"],
                    },
                }}
            >
                {title}
            </Heading>
            {subtitle && (
                <Heading
                    as="h2"
                    textStyle="H2"
                    maxW={["100%", "100%", "800px"]}
                    color={
                        extraStyles?.subtitle
                            ? extraStyles?.subtitle
                            : "text.base"
                    }
                    mt={["8px", "8px", 0]}
                    mb={[1, 1, 2]}
                    _dark={{
                        color: "text.baseInverse",
                    }}
                >
                    {subtitle}
                </Heading>
            )}
            <Flex
                direction={["column", "column", "row"]}
                justifyContent="space-between"
                alignItems={["flex-start", "flex-start", "flex-end"]}
            >
                <Flex maxW={["90%", "90%", "700px"]}>
                    <Text
                        textStyle={"title3"}
                        color={
                            withImage
                                ? [
                                      "text.baseInverse",
                                      "text.baseInverse",
                                      "text.base",
                                  ]
                                : "text.base"
                        }
                        _dark={{
                            color: withImage
                                ? ["text.base", "text.base", "text.base"]
                                : "text.baseInverse",
                        }}
                        mb={0}
                    >
                        {text}
                    </Text>
                </Flex>
                {cta && (
                    <Button
                        as={Link}
                        {...(variant ? { variant } : {})}
                        {...(onClick ? { onClick } : {})}
                        {...(link ? { href: link } : {})}
                        mt={[2, 2, 0]}
                        {...(dataTestId ? { "data-testid": dataTestId } : {})}
                    >
                        <>
                            {iconLeft && (
                                <Icon
                                    w={1}
                                    h={1}
                                    as={iconLeft}
                                    verticalAlign="baseline"
                                    mr=".3em"
                                    color="text.baseInverse"
                                />
                            )}
                            {ctaButtonLabel}
                            {iconRight && (
                                <Icon
                                    w={1}
                                    h={1}
                                    as={iconRight}
                                    verticalAlign="baseline"
                                    ml=".3em"
                                    color="text.baseInverse"
                                />
                            )}
                        </>
                    </Button>
                )}
            </Flex>
        </Box>
    );
};
